import { useState, useEffect, useRef } from "react";
import { hexToHsv, hsvToHex, normalizeHex } from "@/utils/brandColor";

// The colour picker in the Primary Color sheet: a saturation / value square
// under the "Color *" heading, then a white panel with the colour swatch, the
// hue slider and the hex field. Pointer-driven, no library.
//
// props: value (hex), onChange(hex)
export default function ColorPicker({ value, onChange }) {
  const [hsv, setHsv] = useState(() => hexToHsv(value));
  const [hexText, setHexText] = useState(() => normalizeHex(value) || "");
  const squareRef = useRef(null);
  const hueRef = useRef(null);
  const lastEmitted = useRef(normalizeHex(value));

  // an outside change (a reset, the sheet reopening) resyncs the picker;
  // our own emits are ignored so the hue survives a grey / black pick
  useEffect(() => {
    const h = normalizeHex(value);
    if (h && h !== lastEmitted.current) { setHsv(hexToHsv(h)); setHexText(h); lastEmitted.current = h; }
  }, [value]);

  const emit = (next) => {
    setHsv(next);
    const hex = hsvToHex(next);
    setHexText(hex);
    lastEmitted.current = hex;
    onChange?.(hex);
  };

  const drag = (ref, onPoint) => (e) => {
    const el = ref.current; if (!el) return;
    const move = (ev) => {
      const r = el.getBoundingClientRect();
      const x = Math.max(0, Math.min(1, (ev.clientX - r.left) / r.width));
      const y = Math.max(0, Math.min(1, (ev.clientY - r.top) / r.height));
      onPoint(x, y);
    };
    const up = () => { window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); };
    window.addEventListener("pointermove", move); window.addEventListener("pointerup", up);
    e.preventDefault();
    move(e);
  };

  const onSquare = drag(squareRef, (x, y) => emit({ ...hsv, s: x, v: 1 - y }));
  const onHue = drag(hueRef, (x) => emit({ ...hsv, h: Math.min(359.99, x * 360) }));

  const onHexInput = (e) => {
    const text = e.target.value;
    setHexText(text);
    const h = normalizeHex(text);
    if (h) { setHsv(hexToHsv(h)); lastEmitted.current = h; onChange?.(h); }
  };
  const onHexBlur = () => { const h = normalizeHex(hexText); setHexText(h || hsvToHex(hsv)); };

  const current = hsvToHex(hsv);
  const hueColor = hsvToHex({ h: hsv.h, s: 1, v: 1 });

  return (
    <div className="color-picker">
      <div
        ref={squareRef}
        className="color-picker-square"
        style={{ background: `linear-gradient(to top, #000, transparent), linear-gradient(to right, #fff, ${hueColor})` }}
        onPointerDown={onSquare}
        role="slider"
        aria-label="Saturation and brightness"
        aria-valuetext={current}
      >
        <span className="color-picker-thumb" style={{ left: `${hsv.s * 100}%`, top: `${(1 - hsv.v) * 100}%` }} />
      </div>
      <div className="color-picker-panel">
        <div className="color-picker-row">
          <span className="color-picker-swatch" style={{ background: current }} />
          <div ref={hueRef} className="color-picker-hue" onPointerDown={onHue} role="slider" aria-label="Hue" aria-valuenow={Math.round(hsv.h)} aria-valuemin={0} aria-valuemax={360}>
            <span className="color-picker-thumb" style={{ left: `${(hsv.h / 360) * 100}%`, top: "50%" }} />
          </div>
        </div>
        <label className="color-picker-hex">
          <input type="text" value={hexText} onChange={onHexInput} onBlur={onHexBlur} maxLength={7} spellCheck={false} autoComplete="off" aria-label="Hex colour" />
          <span>Hex</span>
        </label>
      </div>
    </div>
  );
}
